import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { PlayCircle, Loader2, Search, AlertTriangle, Gauge, ChevronRight, Download, Paperclip, Timer } from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from '../../lib/auth';
import { Input } from '../../components/ui/input';
import { STATE, fmtD, dur, answersOf, filesOf, loc } from './helpers';
import { RecordDetailSheet } from './RecordDetailSheet';
import { ReminderSettingsButton } from './ReminderSettings';
import { DatePresets, localIso } from './DatePresets';
import { useTranslation, useI18nStore } from '../../lib/i18n';
import { loadMapSettings } from '../../lib/useMapSettings';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);


const iso = (d) => d.toISOString().slice(0, 10);
const STATUS_OPTS = [['all', 'allStatuses'], ['pending', 'stPending'], ['started', 'stStarted'], ['stopped', 'stStopped']];
const uniq = (rows, id, name) => [...new Map(rows.filter((r) => r[id]).map((r) => [r[id], r[name] || r[id]])).entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'tr'));
const selCls = 'h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm max-w-[180px]';
const Pick = ({ value, onChange, all, opts, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={selCls} data-testid={testId}>
    <option value="">{all}</option>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const Badge = ({ state }) => {
  const [lbl, cls] = STATE[state];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap ${cls}`}>{lbl}</span>;
};
const Times = ({ r }) => (
  <span className="tabular-nums whitespace-nowrap">
    {r.start?.entered_time || '—'}<span className="text-slate-300 mx-1">→</span>{r.stop?.entered_time || '—'}
    {(r.start?.time_changed || r.stop?.time_changed) && <AlertTriangle className="inline h-3 w-3 ml-1 text-amber-500" title={tt('timeChanged')} />}
  </span>
);

const Work = ({ w }) => (w?.work_total_min == null ? <span className="text-slate-300">—</span> : (
  <span className="inline-flex flex-col items-end leading-tight">
    <b className="tabular-nums">{dur(w.work_total_min)}</b>
    <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap">{w.depart_at} → {w.return_at}{w.legs_estimated ? ' ~' : ''}</span>
  </span>
));

function exportXlsx(rows) {
  const data = rows.map((r) => ({
    [tt('xDate')]: r.date, [tt('xEmployee')]: r.employee_name, [tt('xRoute')]: r.route_code, [tt('xDepot')]: r.depot_name, [tt('xStatus')]: STATE[r.state][0], [tt('xRequired')]: r.required ? tt('yes') : tt('no'),
    [tt('xStartTime')]: r.start?.entered_time, [tt('xStartSent')]: r.start && new Date(r.start.submitted_at).toLocaleTimeString(loc()),
    [tt('xStartKm')]: r.start?.km, [tt('xStartAddr')]: r.start?.address, [tt('xStartCoord')]: r.start && `${r.start.lat},${r.start.lng}`,
    [tt('xStopTime')]: r.stop?.entered_time, [tt('xStopSent')]: r.stop && new Date(r.stop.submitted_at).toLocaleTimeString(loc()),
    [tt('xStopKm')]: r.stop?.km, [tt('xStopAddr')]: r.stop?.address,
    [tt('xStopCoord')]: r.stop && `${r.stop.lat},${r.stop.lng}`, [tt('xTotalKm')]: r.km_total, [tt('xDuration')]: r.duration_min, [tt('xCorrected')]: r.correction_used ? tt('yes') : tt('no'),
    ...Object.fromEntries(answersOf(r, 'start').map(([l, v]) => [`Start: ${l}`, v])),
    ...Object.fromEntries(answersOf(r, 'stop').map(([l, v]) => [`Stop: ${l}`, v])),
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Start-Stop');
  XLSX.writeFile(wb, `start-stop_${rows[rows.length - 1]?.date || ''}_${rows[0]?.date || ''}.xlsx`);
}

function RecordsTable({ rows, onOpen }) {
  return (
    <table className="hidden md:table w-full text-sm" data-testid="ss-records-table">
      <thead className="text-[11px] uppercase tracking-wider text-slate-400">
        <tr>{[tt('thEmployee'), tt('thDate'), tt('thStatus'), 'Start → Stop', tt('thDuration'), tt('thWork'), 'KM', ''].map((h, i) => <th key={i} className={`px-4 py-3 font-semibold ${i >= 4 && i < 7 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.assignment_id} onClick={() => onOpen(r)} data-testid={`ss-row-${r.assignment_id}`}
            className={`border-t border-slate-100 dark:border-slate-800 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${r.required && r.state === 'missing' ? 'bg-rose-50/50 dark:bg-rose-950/20' : ''}`}>
            <td className="px-4 py-3">
              <div className="font-semibold">{r.employee_name}</div>
              <div className="text-xs text-slate-500">{r.route_code || '—'} · {r.depot_name || '—'}</div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtD(r.date)}</td>
            <td className="px-4 py-3"><Badge state={r.state} />{r.correction_used && <span className="ml-1 text-[10px] font-bold text-violet-600">{tt('corrShort')}</span>}</td>
            <td className="px-4 py-3"><Times r={r} /></td>
            <td className="px-4 py-3 text-right tabular-nums text-slate-600">{dur(r.duration_min)}</td>
            <td className="px-4 py-3 text-right" data-testid={`ss-row-work-${r.assignment_id}`}><Work w={r.work} /></td>
            <td className="px-4 py-3 text-right font-bold tabular-nums">{r.km_total != null ? r.km_total : '—'}</td>
            <td className="px-3 py-3 text-right">
              <button type="button" className="inline-flex items-center gap-0.5 text-xs font-semibold text-sky-700 hover:underline" data-testid={`ss-row-toggle-${r.assignment_id}`}>
                {tt('details')}<ChevronRight className="h-3.5 w-3.5" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecordsCards({ rows, onOpen }) {
  return (
    <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800" data-testid="ss-records-cards">
      {rows.map((r) => (
        <button key={r.assignment_id} type="button" onClick={() => onOpen(r)} className="w-full text-left p-4 flex items-center gap-3 active:bg-slate-50" data-testid={`ss-card-${r.assignment_id}`}>
          <span className={`h-10 w-1 rounded-full ${STATE[r.state][2]}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2"><span className="font-semibold truncate">{r.employee_name}</span><Badge state={r.state} /></div>
            <div className="text-xs text-slate-500 truncate">{fmtD(r.date)} · {r.route_code || '—'} · {r.depot_name || '—'}</div>
            <div className="text-xs mt-1 flex items-center gap-3"><Times r={r} />{r.work?.work_total_min != null && <span className="tabular-nums text-slate-600" data-testid={`ss-card-work-${r.assignment_id}`}>{dur(r.work.work_total_min)}</span>}{r.km_total != null && <b className="tabular-nums">{r.km_total} km</b>}{filesOf(r).length > 0 && <Paperclip className="h-3 w-3 text-slate-400" />}</div>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-300" />
        </button>
      ))}
    </div>
  );
}

export default function StartStopRecords() {
  const today = new Date();
  const [from, setFrom] = useState(localIso(today));
  const [to, setTo] = useState(localIso(today));
  useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [status, setStatus] = useState('all');
  const [routeF, setRouteF] = useState('');
  const [depotF, setDepotF] = useState('');
  const [empF, setEmpF] = useState('');
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get('/planning/start-stop/records', { params: { date_from: from, date_to: to } })).data?.data); }
    catch (e) { toast.error(e?.response?.data?.detail || tt('loadFailed')); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMapSettings(); }, []);

  const reset = async (r) => {
    setResetting(r.assignment_id);
    try { await api.delete(`/planning/assignments/${r.assignment_id}/start-stop`); toast.success(tt('resetDone')); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || tt('resetFailed')); }
    finally { setResetting(null); }
  };

  const shift = (d, days) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + days); return iso(x); };
  const changeFrom = (v) => {
    if (!v) return;
    setFrom(v);
    if (to < v) setTo(v);
    else if ((new Date(to) - new Date(v)) / 86400000 > 62) setTo(shift(v, 62));
  };
  const changeTo = (v) => {
    if (!v) return;
    setTo(v);
    if (from > v) setFrom(v);
    else if ((new Date(v) - new Date(from)) / 86400000 > 62) setFrom(shift(v, -62));
  };

  const all = useMemo(() => data?.rows || [], [data]);
  const rows = useMemo(() => all.filter((r) => {
    if (stateFilter === 'missing') { if (!(r.required && r.state === 'missing')) return false; }
    else if (stateFilter === 'time_changed') { if (!(r.start?.time_changed || r.stop?.time_changed)) return false; }
    else if (stateFilter === 'corrected') { if (!r.correction_used) return false; }
    else if (stateFilter !== 'all' && r.state !== stateFilter) return false;
    if (status === 'pending' ? !['not_started', 'missing'].includes(r.state) : status !== 'all' && r.state !== status) return false;
    if (routeF && r.route_id !== routeF) return false;
    if (depotF && r.depot_id !== depotF) return false;
    if (empF && r.employee_id !== empF) return false;
    const s = q.trim().toLowerCase();
    return !s || `${r.employee_name} ${r.route_code} ${r.depot_name}`.toLowerCase().includes(s);
  }), [all, q, stateFilter, status, routeF, depotF, empF]);
  const routeOpts = useMemo(() => uniq(all.filter((r) => !depotF || r.depot_id === depotF), 'route_id', 'route_code'), [all, depotF]);
  const depotOpts = useMemo(() => uniq(all, 'depot_id', 'depot_name'), [all]);
  const empOpts = useMemo(() => uniq(all, 'employee_id', 'employee_name'), [all]);
  const anyFilter = status !== 'all' || stateFilter !== 'all' || routeF || depotF || empF || q;
  const openRec = all.find((r) => r.assignment_id === openId) || null;
  const s = data?.summary;
  const chip = (k, label, val, tone) => (
    <button type="button" key={k} onClick={() => { setStateFilter(stateFilter === k ? 'all' : k); setStatus('all'); }} data-testid={`ss-filter-${k}`}
      className={`rounded-xl px-4 py-3 text-left transition-colors ${stateFilter === k ? 'bg-slate-900 text-white' : 'bg-white dark:bg-slate-900 hover:bg-slate-50 border border-slate-200/70 dark:border-slate-800'}`}>
      <div className="text-[11px] opacity-60">{label}</div><div className={`text-2xl font-black tabular-nums ${stateFilter === k ? '' : tone}`}>{val ?? 0}</div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="start-stop-records-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2"><PlayCircle className="h-7 w-7 text-emerald-600" />{tt('title')}</h1>
          <p className="text-sm text-slate-500">{tt('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={from} onChange={(e) => changeFrom(e.target.value)} className="h-9 w-40" data-testid="ss-date-from" />
          <span className="text-slate-400">→</span>
          <Input type="date" value={to} onChange={(e) => changeTo(e.target.value)} className="h-9 w-40" data-testid="ss-date-to" />
          <ReminderSettingsButton />
          <button type="button" onClick={() => exportXlsx(rows)} disabled={!rows.length} className="h-9 px-3 rounded-lg border text-sm font-semibold flex items-center gap-1 disabled:opacity-40" data-testid="ss-export-btn"><Download className="h-4 w-4" />Excel</button>
        </div>
      <DatePresets from={from} to={to} onPick={(a, b) => { setFrom(a); setTo(b); }} />
      </div>
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {chip('all', tt('kTotal'), s.total, 'text-slate-900 dark:text-white')}
          {chip('stopped', tt('stStopped'), s.stopped, 'text-emerald-600')}
          {chip('started', tt('stStarted'), s.started, 'text-sky-600')}
          {chip('missing', tt('kMissing'), s.missing_required, 'text-rose-600')}
          {chip('time_changed', tt('kTimeChanged'), s.time_changed, 'text-amber-600')}
          <div className="rounded-xl px-4 py-3 bg-slate-100 dark:bg-slate-800"><div className="text-[11px] text-slate-500 flex items-center gap-1"><Gauge className="h-3 w-3" />{tt('kOdometer')}</div><div className="text-2xl font-black tabular-nums">{s.km_total}</div></div>
          <div className="rounded-xl px-4 py-3 bg-slate-900 text-white" data-testid="ss-kpi-work">
            <div className="text-[11px] text-slate-300 flex items-center gap-1"><Timer className="h-3 w-3" />{tt('kWork')}</div>
            <div className="text-2xl font-black tabular-nums" data-testid="ss-kpi-work-total">{s.work_count ? dur(s.work_total_min) : '—'}</div>
            <div className="text-[10px] text-slate-400" data-testid="ss-kpi-work-avg">{s.work_count ? tt('kWorkAvg', { d: dur(s.work_avg_min), n: s.work_count }) : tt('kWorkNone')}</div>
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-100 dark:border-slate-800" data-testid="ss-filters">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tt('search')} className="h-9 pl-8 w-44" data-testid="ss-search" />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setStateFilter('all'); }} className={selCls} data-testid="ss-status-filter">
            {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{tt(l)}</option>)}
          </select>
          <Pick value={depotF} onChange={(v) => { setDepotF(v); setRouteF(''); }} all={tt('allDepots')} opts={depotOpts} testId="ss-depot-filter" />
          <Pick value={routeF} onChange={setRouteF} all={tt('allRoutes')} opts={routeOpts} testId="ss-route-filter" />
          <Pick value={empF} onChange={setEmpF} all={tt('allEmployees')} opts={empOpts} testId="ss-employee-filter" />
          {anyFilter && <button type="button" onClick={() => { setStatus('all'); setStateFilter('all'); setRouteF(''); setDepotF(''); setEmpF(''); setQ(''); }} className="h-9 px-2 text-xs text-slate-500 hover:text-slate-900 underline" data-testid="ss-filters-clear">{tt('clear')}</button>}
          <span className="ml-auto text-xs text-slate-400 flex items-center gap-1">{loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{tt(rows.length === 1 ? 'nRecords1' : 'nRecords', { n: rows.length })}</span>
        </div>
        {rows.length === 0 ? <p className="py-14 text-center text-sm text-slate-400" data-testid="ss-empty">{tt('noRecords')}</p> : (
          <>
            <RecordsTable rows={rows} onOpen={(r) => setOpenId(r.assignment_id)} />
            <RecordsCards rows={rows} onOpen={(r) => setOpenId(r.assignment_id)} />
          </>
        )}
      </div>
      <RecordDetailSheet r={openRec} onClose={() => setOpenId(null)} onReset={reset} resetting={resetting === openId} onChanged={load} />
    </div>
  );
}
